import { create } from 'zustand';
import { CATEGORIES } from '../config/categories';
import { logger } from '../../../shared/utils/logger';

export interface CatastralCategory {
  id: string;
  label: string;
  color: string;
  computo: number | null;
  isCustom: boolean;
  fechaCreacion: Date;
  fechaModificacion: Date;
}

export interface CategoriesState {
  // Estado
  categories: CatastralCategory[];
  customCategories: CatastralCategory[];
  
  // Acciones
  initializeCategories: () => void;
  updateCategory: (id: string, updates: Partial<Omit<CatastralCategory, 'id' | 'fechaCreacion' | 'isCustom'>>) => void;
  createCustomCategory: (categoryData: Omit<CatastralCategory, 'fechaCreacion' | 'fechaModificacion' | 'isCustom'>) => void;
  deleteCustomCategory: (id: string) => boolean;
  getCategoryById: (id: string) => CatastralCategory | null;
  getAllCategories: () => CatastralCategory[];
  getCustomCategories: () => CatastralCategory[];
  getDefaultCategories: () => CatastralCategory[];
  getCategoryLabel: (categoryId: string) => string;
  getCategoryColor: (categoryId: string) => string;
  getCategoryComputo: (categoryId: string) => number | null;
  isZonasComunesEdificio: (categoryId: string) => boolean;
  exportToCSV: () => string;
  importFromCSV: (csvContent: string) => { success: number; errors: string[] };

  clearStorage: () => void;
}

export const useCategoriesStore = create<CategoriesState>()(
  (set, get) => {
    // Limpiar cualquier estado persistido anterior
    try {
              sessionStorage.removeItem('catastral-categories-storage');
        sessionStorage.removeItem('categoriesConfig');
        sessionStorage.removeItem('customCategories');
        logger.log('CATEGORIES_STORE', '🧹 CategoriesStore: sessionStorage limpiado completamente');
    } catch (error) {
              logger.warn('CATEGORIES_STORE', '⚠️ CategoriesStore: Error al limpiar sessionStorage:', error);
    }

    // Inicializar inmediatamente al crear el store
    const now = new Date();
    
    // Validar que CATEGORIES existe y es válido
    if (!CATEGORIES || !Array.isArray(CATEGORIES) || CATEGORIES.length === 0) {
      logger.error('CATEGORIES_STORE', '❌ CategoriesStore: CATEGORIES no es un array válido!');
      throw new Error('CATEGORIES debe ser un array válido con al menos una categoría. Verifica src/apps/bim-catastro/config/categories.ts');
    }
    
    // Mapear CATEGORIES a CatastralCategory (fuente de verdad única)
    const defaultCategories: CatastralCategory[] = CATEGORIES.map(cat => {
      if (!cat.id) {
        logger.error('CATEGORIES_STORE', '❌ CategoriesStore: Categoría sin ID:', cat);
        throw new Error(`Categoría sin ID encontrada: ${JSON.stringify(cat)}`);
      }
      
      return {
        id: cat.id,
        label: cat.label,
        color: cat.color,
        computo: cat.computo,
        isCustom: false,
        fechaCreacion: now,
        fechaModificacion: now
      };
    });
    
    logger.log('CATEGORIES_STORE', `✅ CategoriesStore: ${defaultCategories.length} categorías inicializadas desde config/categories.ts`);

    return {
      // Estado inicial con categorías por defecto
      categories: defaultCategories,
      customCategories: [],

                           // Inicializar categorías por defecto
      initializeCategories: () => {
        const { categories } = get();
        
        // Solo inicializar si no hay categorías o si no hay categorías por defecto
        const hasDefaultCategories = categories.some(cat => !cat.isCustom);
        
        if (!hasDefaultCategories) {
          const now = new Date();
          const defaultCategories: CatastralCategory[] = CATEGORIES.map((cat: any) => ({
            id: cat.id,
            label: cat.label,
            color: cat.color,
            computo: cat.computo,
            isCustom: false,
            fechaCreacion: now,
            fechaModificacion: now
          }));

          // Preservar categorías personalizadas existentes
          const existingCustomCategories = categories.filter(cat => cat.isCustom);
          
          set({ 
            categories: [...defaultCategories, ...existingCustomCategories],
            customCategories: existingCustomCategories
          });
          logger.log('CATEGORIES_STORE', '✅ CategoriesStore: Categorías por defecto inicializadas (preservando personalizadas)');
        } else {
          logger.log('CATEGORIES_STORE', '✅ CategoriesStore: Categorías ya inicializadas, no se reinicializan');
        }
        logger.log('CATEGORIES_STORE', '🔍 CategoriesStore: Estado actual:', get().categories);
      },

      // Actualizar una categoría existente
      updateCategory: (id: string, updates: Partial<Omit<CatastralCategory, 'id' | 'fechaCreacion' | 'isCustom'>>) => {
        set(state => ({
          categories: state.categories.map(cat => 
            cat.id === id 
              ? { ...cat, ...updates, fechaModificacion: new Date() }
              : cat
          ),
          customCategories: state.customCategories.map(cat => 
            cat.id === id 
              ? { ...cat, ...updates, fechaModificacion: new Date() }
              : cat
          )
        }));
      },

      // Crear una nueva categoría personalizada
      createCustomCategory: (categoryData: Omit<CatastralCategory, 'fechaCreacion' | 'fechaModificacion' | 'isCustom'>) => {
        const now = new Date();
        const newCategory: CatastralCategory = {
          ...categoryData,
          id: categoryData.id || `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          isCustom: true,
          fechaCreacion: now,
          fechaModificacion: now
        };

        set(state => ({
          categories: [...state.categories, newCategory],
          customCategories: [...state.customCategories, newCategory]
        }));

        logger.log('CATEGORIES_STORE', '✅ CategoriesStore: Nueva categoría personalizada creada:', newCategory);
      },

      // Eliminar una categoría personalizada
      deleteCustomCategory: (id: string) => {
        const { categories } = get();
        const category = categories.find(c => c.id === id);
        
        if (!category || !category.isCustom) {
          logger.warn('CATEGORIES_STORE', '⚠️ CategoriesStore: No se puede eliminar una categoría por defecto:', id);
          return false;
        }

        set(state => ({
          categories: state.categories.filter(c => c.id !== id),
          customCategories: state.customCategories.filter(c => c.id !== id)
        }));

        logger.log('CATEGORIES_STORE', '✅ CategoriesStore: Categoría personalizada eliminada:', id);
        return true;
      },

      // Obtener categoría por ID
      getCategoryById: (id: string) => {
        const { categories } = get();
        return categories.find(c => c.id === id) || null;
      },

             // Obtener todas las categorías
       getAllCategories: () => {
         const { categories } = get();
         return [...categories];
       },

      // Obtener solo categorías personalizadas
      getCustomCategories: () => {
        const { customCategories } = get();
        return [...customCategories];
      },

      // Obtener solo categorías por defecto
      getDefaultCategories: () => {
        const { categories } = get();
        return categories.filter(c => !c.isCustom);
      },

      // Obtener el label de una categoría por ID
      getCategoryLabel: (categoryId: string): string => {
        const { categories } = get();
        const category = categories.find(c => c.id === categoryId);
        return category ? category.label : categoryId;
      },

      // Obtener el color de una categoría por ID
      getCategoryColor: (categoryId: string): string => {
        const { categories } = get();
        const category = categories.find(c => c.id === categoryId);
        return category ? category.color : 'rgba(128, 128, 128, 0.7)';
      },

      // Obtener el cómputo de una categoría por ID
      getCategoryComputo: (categoryId: string): number | null => {
        const { categories } = get();
        const category = categories.find(c => c.id === categoryId);
        return category ? category.computo : null;
      },

      // Verificar si una categoría es "zonas comunes edificio"
      isZonasComunesEdificio: (categoryId: string): boolean => {
        return categoryId === 'ZC';
      },

      // Exportar a CSV
      exportToCSV: () => {
        const { categories } = get();
        
        // Verificar que hay categorías para exportar
        if (!categories || categories.length === 0) {
          logger.warn('CATEGORIES_STORE', '⚠️ CategoriesStore: No hay categorías para exportar');
          return '';
        }
        
        const headers = ['ID', 'Etiqueta', 'Color', 'Cómputo'];
        const rows = categories.map(c => [
          c.id || '',
          c.label || '',
          c.color || '',
          c.computo?.toString() || ''
        ]);

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => `"${cell}"`).join(';'))
          .join('\n');

        logger.log('CATEGORIES_STORE', '✅ CategoriesStore: CSV generado exitosamente');
        return csvContent;
      },

      // Importar desde CSV
      importFromCSV: (csvContent: string) => {
        const lines = csvContent.split('\n').filter(line => line.trim());
        const errors: string[] = [];
        let success = 0;

        logger.log('CATEGORIES_STORE', '📊 CategoriesStore: Iniciando importación CSV');
        logger.log('CATEGORIES_STORE', '📊 CategoriesStore: Número de líneas:', lines.length);

        // Saltar la primera línea (headers)
        for (let i = 1; i < lines.length; i++) {
          try {
            const line = lines[i];
            logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Procesando línea ${i + 1}:`, line);
            
            const cells = line.split(';').map(cell => cell.replace(/^"|"$/g, '').trim());
            logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Celdas procesadas:`, cells);
            
            if (cells.length < 4) {
              errors.push(`Línea ${i + 1}: Número insuficiente de columnas (${cells.length}/4)`);
              continue;
            }

            // Parsear el cómputo de manera más robusta
            let computo: number | null = null;
            if (cells[3] && cells[3].trim() !== '') {
              const computoValue = parseFloat(cells[3]);
              if (!isNaN(computoValue)) {
                computo = computoValue;
              }
            }

            const categoryData = {
              id: cells[0],
              label: cells[1],
              color: cells[2],
              computo: computo
            };

            logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Datos de categoría procesados:`, categoryData);

            // Verificar que el ID no esté vacío
            if (!categoryData.id || categoryData.id.trim() === '') {
              errors.push(`Línea ${i + 1}: ID de categoría vacío`);
              continue;
            }

            // Verificar si ya existe una categoría con ese ID
            const existingCategory = get().getCategoryById(categoryData.id);
            if (existingCategory) {
              logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Actualizando categoría existente: ${categoryData.id}`);
              // Actualizar la categoría existente
              get().updateCategory(existingCategory.id, categoryData);
            } else {
              logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Creando nueva categoría personalizada: ${categoryData.id}`);
              // Crear nueva categoría personalizada
              get().createCustomCategory(categoryData);
            }
            success++;
          } catch (error) {
            logger.error('CATEGORIES_STORE', `❌ CategoriesStore: Error en línea ${i + 1}:`, error);
            errors.push(`Línea ${i + 1}: ${error}`);
          }
        }

        logger.log('CATEGORIES_STORE', `📊 CategoriesStore: Importación completada - ${success} exitosas, ${errors.length} errores`);
        return { success, errors };
      },

             

      // Limpiar sessionStorage y reinicializar
      clearStorage: () => {
        // Limpiar sessionStorage
        sessionStorage.removeItem('catastral-categories-storage');
        
        // Resetear estado
        set({ 
          categories: [],
          customCategories: []
        });
        
        // Reinicializar con valores por defecto
        get().initializeCategories();
        
        logger.log('CATEGORIES_STORE', '✅ CategoriesStore: sessionStorage limpiado y categorías reinicializadas');
      }
    };
  }
);
